/**
 * T049 [US5]: Route guard — direct /apps/:appSlug without grant → Access Denied, redirect to launcher.
 */
import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { getApps } from '@/services/apps';
import { AccessDenied } from '@/components/auth/AccessDenied';

export default function AppAccessGuard() {
  const { appSlug } = useParams<{ appSlug: string }>();
  const navigate = useNavigate();
  const isAdmin = useAuthStore((s) => s.user?.is_admin) ?? false;
  const [allowed, setAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    if (!appSlug) {
      setAllowed(false);
      return;
    }
    if (isAdmin) {
      setAllowed(true);
      return;
    }
    let cancelled = false;
    getApps()
      .then((apps) => {
        if (!cancelled) {
          setAllowed(apps.some((a) => a.route_path === appSlug));
        }
      })
      .catch(() => {
        if (!cancelled) setAllowed(false);
      });
    return () => {
      cancelled = true;
    };
  }, [appSlug, isAdmin]);

  useEffect(() => {
    if (allowed === false) {
      navigate('/', { replace: true });
    }
  }, [allowed, navigate]);

  if (allowed === null) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center p-8">
        <p className="text-muted-foreground">Checking access...</p>
      </div>
    );
  }

  if (allowed === false) {
    return <AccessDenied />;
  }

  return <Outlet />;
}
